import React, { useEffect, useState, useRef } from "react";
import { Navigate } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useAuth } from "../hooks/useAuth";
import { supabase } from "../config/supabase";
import { captureConnectAiFromSearch } from "../utils/connectAiPopup";

const TOKEN_SYNC_TIMEOUT_MS = 5000;

export default function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const [tokenAvailable, setTokenAvailable] = useState(() => !!localStorage.getItem('access_token'));
  const [syncTimedOut, setSyncTimedOut] = useState(false);
  const syncAttemptedRef = useRef(false);

  const needsSync = !loading && !!user && !tokenAvailable;

  // When user becomes available but token is missing, try to recover it
  useEffect(() => {
    if (!needsSync) {
      syncAttemptedRef.current = false;
      setSyncTimedOut(false);
      return;
    }

    if (syncAttemptedRef.current) return;
    syncAttemptedRef.current = true;

    let cancelled = false;

    const tryRecoverToken = async () => {
      try {
        if (!supabase) {
          if (!cancelled) setSyncTimedOut(true);
          return;
        }
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token && !cancelled) {
          localStorage.setItem('access_token', session.access_token);
          setTokenAvailable(true);
          return;
        }
      } catch (e) {
        // ignore
      }
      if (!cancelled) setSyncTimedOut(true);
    };

    tryRecoverToken();

    const timeout = setTimeout(() => {
      if (!cancelled) setSyncTimedOut(true);
    }, TOKEN_SYNC_TIMEOUT_MS);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [needsSync]);

  // Keep tokenAvailable in sync when auth state changes
  useEffect(() => {
    if (user) {
      const token = localStorage.getItem('access_token');
      if (token) setTokenAvailable(true);
    } else {
      setTokenAvailable(false);
    }
  }, [user]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          gap: 2
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Verificando autenticação...
        </Typography>
      </Box>
    );
  }

  if (!user || syncTimedOut) {
    localStorage.removeItem('access_token');
    // O <Navigate> troca a URL antes dos effects. O motivo do popup tem de ser
    // guardado aqui, senão o e-mail ?connect_ai= perde o parâmetro no login.
    captureConnectAiFromSearch(typeof window !== 'undefined' ? window.location.search : '');
    return <Navigate to="/acess" />;
  }

  if (!tokenAvailable) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          gap: 2
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Sincronizando autenticação...
        </Typography>
      </Box>
    );
  }

  return children;
}
